<template>
    <div class="fixed top-0 left-0 z-20 w-full h-full overflow-y-auto bg-white outline-none" id="form-step-4">
        <div class="container flex items-center justify-center h-screen mx-auto">
            <div class="p-8 mx-4 border-2 rounded-lg shadow-md md:mx-auto sm:w-75 md:w-50">
                <div class="text-center">
                    <img :src="logoSrc" alt="Unbundled Legal Help" class="mx-auto img-responsive">
                </div>

                <div class="flex flex-row items-center justify-between pt-8">
                    <div>
                        <i class="fas fa-sync fa-spin fa-3x"></i>
                    </div>

                    <div class="pl-8">
                        <h3 class="text-xl md:text-2xl">
                            Dear {{ name }},
                        </h3>

                        <span class="text-sm md:text-base lg:text-lg">
                            Just a moment while we connect you with {{ sectorWithArticle }} attorney that serves {{ location }}&#8230;
                        </span>

                        <p v-if="link" class="text-sm md:text-base lg:text-lg mt-2">If not automatically redirected, <a class="text-brand-blue underline hover:text-brand-blue-dark" :href="link">click here</a> to view your provider details.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
const REDIRECT_TIMEOUT = 4000;

export default {
    props: {
        lcsForm: {
            type: Object,
            required: false,
        },
        logoSrc: {
            type: String,
            required: true,
        },
        sectorWithArticle: {
          type: String,
          required: false,
          default: 'an'
      },
        location: {
          type: String,
          required: false,
          default: 'your local area'
      },
        name: {
            type: String,
            required: true
        },
        payload: {
            type: Object,
            required: true,
        },
    },
    data () {
        return {
            'link': null,
            form: {},
            submitting: false,
        }
    },

    beforeMount() {
        this.$store.commit('meta/disablePopup');
    },

    beforeUnmount() {
        this.$store.commit('meta/enablePopup');
    },

    mounted() {
        // BEFORE this.submit(), order matters.
        if (this.lcsForm) {
            this.form = this.lcsForm;
        }

        if (this.payload.intake_method !== 'text_convo') {
            window.dataLayer.push({
                event: 'form_step_mounted',
                form_step_id: window.getCurrentStep(),
                form_step_name: 'conversion'
            });
        }

        this.submit();
    },

    methods: {
        /**
         * Submit lead's data and redirect to appropriate url.
         */
        async submit() {
            this.submitting = true;

            if (this.payload.intake_method !== 'text_convo') {
                window.dataLayer.push({
                    event: 'form_step_submit',
                    form_step_id: window.getCurrentStep(),
                    form_step_name: 'conversion'
                });

                // AFTER form_step_submit, order matters.
                window.dataLayer.push({
                    event: 'form_submit',
                    form_id: 'ulh-lead-intake',
                    form_name: 'ULH Lead Intake'
                });
            }

            axios.post('/requests/post', this.payload).then(({ data }) => {
                const redirectUrl = '/requests/conversion';

                this.clearSavedFormFields();

                setTimeout(() => {
                    this.redirect(redirectUrl);
                    this.link = redirectUrl;
                }, REDIRECT_TIMEOUT + (REDIRECT_TIMEOUT * 0.1));
            }).catch((exception) => {
                this.payload.intake_method === 'text_convo'
                   ? this.handleConversationError(exception)
                   : this.handleLegacyError(exception);
            });
        },

        handleLegacyError(exception) {
            if (exception?.response?.status != 422) {
                if (window.bugsnagClient) {
                    window.bugsnagClient.notify(exception);
                }
            }

            if (exception?.response?.status === 400) {
                this.form.errors.errors = { error: 'Oops, something went wrong on our end. Please try again later.', data: exception?.response?.data };
            } else if (exception?.response?.data?.errors !== undefined) {
                if (exception.response.data.errors) {
                    this.form.errors.errors = exception.response.data.errors;
                }

                if (this.form.errors.onlyNonDisplayableErrors()) {
                    // Errors here likely mean there is a programmatic issue on the frontend
                    this.form.errors.errors = { error: 'Oops, something went wrong on our end. Please try again later.', data: exception.response.data };
                }
            } else if (exception?.response?.data !== undefined) {
                // Errors returned from PostLead::handle
                this.form.errors.errors = exception.response.data;
            } else {
                // No response from server (network error, CORS, timeout, aborted)
                this.form.errors.errors = { error: 'Oops, something went wrong on our end. Please try again later.' };
            }

            this.submitting = false;

            if (this.form.errors.onlyDisplayableErrors()
                && (this.form.errors.has('issue') || this.form.errors.has('overridden_division_slug'))) {
                this.$event.$emit('step:specific', 2);
                return;
            }

            this.$event.$emit('step:previous');
        },
        handleConversationError(exception) {
            let message = 'Oops, something went wrong on our end. Please try again later.';

            const errors = exception.response?.data?.errors;
            if (errors && Object.keys(errors).length > 0) {
                message = Object.values(errors).flat().join("\n");
            }

            if (exception.response?.data?.error) {
                message = exception.response.data.error;
            }

            this.submitting = false;

            this.$emit('error', {message: message, canCorrect: !exception.response?.data?.error});
        },


        /**
         * @param {string} redirectUrl
         * @returns {void}
         */
        redirect (redirectUrl) {
            if (! this.shouldRedirectAfterSubmission()) {
                return;
            }

            window.setTimeout(() => {
                window.location.href = redirectUrl;
            }, 100);
        },

        /**
         * @returns {boolean}
         */
        shouldRedirectAfterSubmission () {
            return ! (window.autoRedirectAfterSubmission === false);
        },

        /**
         * @returns {void}
         */
        clearSavedFormFields () {
            window.localStorage.removeItem('lcsForm');
            window.localStorage.removeItem('alIntakeData');
        }
    }
};
</script>
